import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { INPUT_TYPE_METADATA } from '../GuiBuilder/utils/constants';
import { ValueSchemaEditor } from '../FamiliesPage/components/FamilyEditor/FeatureProperties/ValueSchemaEditor';
import { emptyPresentationContent, featureStatus } from '../../utils/presentationCatalog';
import { normalizeValueSchema } from '../../utils/valuePresentation';
import TemplateValueField from '../../components/TemplateValueField';
import ReleasePageHeader from '../DeployPage/components/ReleasePageHeader';

const inputOptions = Object.entries(INPUT_TYPE_METADATA).map(([value, meta]) => ({
    value: value.toLowerCase(),
    label: meta.label
}));

const seedMissingFeatures = (content, featureData) => {
    const features = { ...(content?.features || {}) };
    featureData.forEach(feature => {
        if (features[feature.name]) {
            const values = { ...(features[feature.name].values || {}) };
            (feature.values || []).forEach((value, index) => {
                if (values[value.value]) return;
                values[value.value] = {
                    label: value.displayText || value.value,
                    description: value.description || '',
                    imageUrl: value.imageUrl || '',
                    propertyValues: value.propertyValues || {},
                    sortOrder: Number(value.sortOrder || index) || 0,
                    isDefault: Boolean(value.isDefault)
                };
            });
            features[feature.name] = { ...features[feature.name], values };
            return;
        }
        features[feature.name] = {
            label: feature.property?.displayText || feature.name,
            description: feature.property?.description || '',
            inputType: String(feature.property?.type || 'dropdown').toLowerCase(),
            valueSchema: feature.property?.valueSchema || [],
            cardLayout: feature.property?.cardLayout || null,
            show: feature.property?.show !== false,
            viewOrder: feature.property?.viewOrder || 0,
            values: Object.fromEntries((feature.values || []).map((value, index) => [value.value, {
                label: value.displayText || value.value,
                description: value.description || '',
                imageUrl: value.imageUrl || '',
                propertyValues: value.propertyValues || {},
                sortOrder: Number(value.sortOrder || index) || 0,
                isDefault: Boolean(value.isDefault)
            }]))
        };
    });
    return { features };
};

const blankFeature = () => ({
    label: '',
    description: '',
    inputType: 'dropdown',
    valueSchema: [],
    cardLayout: null,
    values: {}
});

export default function GuiDataPage({ projectId, linkedFamilies }) {
    const [familyId, setFamilyId] = useState(linkedFamilies?.[0]?.id || '');
    const [features, setFeatures] = useState([]);
    const [presentationSet, setPresentationSet] = useState(null);
    const [content, setContent] = useState(emptyPresentationContent());
    const [selectedName, setSelectedName] = useState('');
    const [selectedValueKey, setSelectedValueKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!familyId && linkedFamilies?.length) {
            setFamilyId(linkedFamilies[0].id);
        }
    }, [familyId, linkedFamilies]);

    const load = useCallback(async () => {
        if (!projectId || !familyId) return;
        setLoading(true);
        setMessage('');
        try {
            const [featureRes, ensureRes] = await Promise.all([
                apiFetch(`/api/products/${familyId}/features`),
                apiFetch(`/api/projects/${projectId}/presentation-sets/ensure`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productFamilyId: familyId, name: 'Standard' })
                })
            ]);
            if (!featureRes.ok) throw new Error(await featureRes.text());
            if (!ensureRes.ok) throw new Error(await ensureRes.text());
            const featurePayload = await featureRes.json();
            const featureData = Array.isArray(featurePayload) ? featurePayload : [];
            const set = await ensureRes.json();
            setFeatures(featureData);
            setPresentationSet(set);
            setContent(seedMissingFeatures(set.content?.features ? set.content : emptyPresentationContent(), featureData));
            setSelectedName(current => current || (featureData[0]?.name ?? ''));
        } catch (err) {
            setMessage(err.message || 'Could not load GUI data.');
        } finally {
            setLoading(false);
        }
    }, [projectId, familyId]);

    useEffect(() => {
        load();
    }, [load]);

    const selectedFeature = features.find(feature => feature.name === selectedName) || null;
    const catalogFeature = content.features?.[selectedName] || blankFeature();
    const statusByName = useMemo(() => {
        const map = {};
        features.forEach(feature => {
            map[feature.name] = featureStatus(feature, content.features?.[feature.name]);
        });
        return map;
    }, [features, content]);

    const updateFeature = (patch) => {
        setContent(current => ({
            ...current,
            features: {
                ...current.features,
                [selectedName]: {
                    ...blankFeature(),
                    ...current.features?.[selectedName],
                    ...patch
                }
            }
        }));
    };

    const updateValue = (valueKey, patch) => {
        const values = { ...(catalogFeature.values || {}) };
        values[valueKey] = { ...(values[valueKey] || {}), ...patch };
        updateFeature({ values });
    };

    const save = async () => {
        if (!presentationSet?.id) return;
        setSaving(true);
        setMessage('');
        try {
            const res = await apiFetch(`/api/projects/${projectId}/presentation-sets/${presentationSet.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content })
            });
            if (!res.ok) throw new Error(await res.text());
            const saved = await res.json();
            setPresentationSet(saved);
            setContent(saved.content?.features ? saved.content : content);
            setMessage('GUI data saved.');
        } catch (err) {
            setMessage(err.message || 'Could not save GUI data.');
        } finally {
            setSaving(false);
        }
    };

    if (!projectId) {
        return <p style={{ color: 'var(--text-secondary)' }}>Choose a project before editing GUI data.</p>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minHeight: 0, height: '100%' }}>
            <ReleasePageHeader
                title="GUI data"
                description="Labels, controls, and value templates shown for a family."
            >
                <select
                    className="input-field"
                    aria-label="Family"
                    value={familyId}
                    onChange={event => { setFamilyId(event.target.value); setSelectedName(''); setSelectedValueKey(''); }}
                    style={{ width: '260px', margin: 0, padding: '0.45rem 0.65rem', fontSize: '0.82rem' }}
                >
                    {(linkedFamilies || []).map(family => (
                        <option key={family.id} value={family.id}>{family.name}</option>
                    ))}
                </select>
                <span style={{
                    padding: '0.25rem 0.55rem',
                    borderRadius: '999px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                }}>
                    Catalog: {presentationSet?.name || 'Standard'}
                </span>
                <button
                    type="button"
                    className="btn-primary"
                    onClick={save}
                    disabled={saving || !presentationSet}
                    style={{ margin: 0, padding: '0.45rem 0.75rem', fontSize: '0.82rem' }}
                >
                    {saving ? 'Saving...' : 'Save GUI data'}
                </button>
            </ReleasePageHeader>
            {message && <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{message}</p>}
            {loading ? (
                <div className="loader"></div>
            ) : (
                <PaneGrid>
                    <Pane title="Features" style={{ gridColumn: 1, gridRow: 1 }}>
                        {features.map(feature => {
                            const status = statusByName[feature.name];
                            return (
                                <ListButton
                                    key={feature.id || feature.name}
                                    active={selectedName === feature.name}
                                    label={feature.name}
                                    badge={status?.incomplete ? 'Missing' : status?.unused ? 'Unused' : ''}
                                    badgeTitle={missingBadgeTitle(status, content.features?.[feature.name])}
                                    badgeColor={status?.incomplete ? 'var(--error)' : 'var(--text-secondary)'}
                                    onClick={() => { setSelectedName(feature.name); setSelectedValueKey(''); }}
                                />
                            );
                        })}
                        {features.length === 0 && <EmptyNote>This family has no features yet.</EmptyNote>}
                    </Pane>
                    <Pane title="Feature" style={{ gridColumn: 3, gridRow: 1 }}>
                        {!selectedFeature ? (
                            <EmptyNote>Select a feature to edit its label, control, and value template.</EmptyNote>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <h3 style={{ margin: 0 }}>{selectedFeature.name}</h3>
                                <FieldLabel label="Label">
                                    <input className="input-field" value={catalogFeature.label || ''} onChange={event => updateFeature({ label: event.target.value })} style={{ margin: 0 }} />
                                </FieldLabel>
                                <FieldLabel label="Description">
                                    <textarea className="textarea-field" value={catalogFeature.description || ''} onChange={event => updateFeature({ description: event.target.value })} style={{ margin: 0, minHeight: '70px' }} />
                                </FieldLabel>
                                <FieldLabel label="Default control">
                                    <select className="input-field" value={String(catalogFeature.inputType || 'dropdown').toLowerCase()} onChange={event => updateFeature({ inputType: event.target.value })} style={{ margin: 0 }}>
                                        {inputOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </FieldLabel>
                                <ValueSchemaEditor valueSchema={catalogFeature.valueSchema || []} onChange={valueSchema => updateFeature({ valueSchema })} />
                            </div>
                        )}
                    </Pane>
                    <Pane title="Values" style={{ gridColumn: 1, gridRow: 3 }}>
                        {!selectedFeature ? (
                            <EmptyNote>Select a feature to see its values.</EmptyNote>
                        ) : (
                            <>
                                {(selectedFeature.values || []).map(value => {
                                    const key = value.value;
                                    const entry = catalogFeature.values?.[key] || {};
                                    return (
                                        <ListButton
                                            key={value.id || key}
                                            active={selectedValueKey === key}
                                            label={key}
                                            badge={entry.label || ''}
                                            badgeColor="var(--text-secondary)"
                                            onClick={() => setSelectedValueKey(key)}
                                        />
                                    );
                                })}
                                {(selectedFeature.values || []).length === 0 && <EmptyNote>This feature has no values.</EmptyNote>}
                            </>
                        )}
                    </Pane>
                    <Pane title="Value" style={{ gridColumn: 3, gridRow: 3 }}>
                        {!selectedFeature || !selectedValueKey ? (
                            <EmptyNote>Select a value to edit its label and template fields.</EmptyNote>
                        ) : (
                            <ValueEditor
                                valueKey={selectedValueKey}
                                entry={catalogFeature.values?.[selectedValueKey] || {}}
                                schema={normalizeValueSchema(catalogFeature.valueSchema || [])}
                                onChange={patch => updateValue(selectedValueKey, patch)}
                                onSetDefault={checked => {
                                    const values = { ...(catalogFeature.values || {}) };
                                    (selectedFeature.values || []).forEach(item => {
                                        values[item.value] = { ...(values[item.value] || {}), isDefault: item.value === selectedValueKey && checked };
                                    });
                                    updateFeature({ values });
                                }}
                            />
                        )}
                    </Pane>
                </PaneGrid>
            )}
        </div>
    );
}

const paneStyle = {
    minHeight: 0,
    overflow: 'hidden',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    background: 'var(--bg-primary)',
    display: 'flex',
    flexDirection: 'column'
};

const SPLIT_KEY = 'gui-data-pane-split';
const MIN_SPLIT = 0.18;
const MAX_SPLIT = 0.82;

function readSplit() {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(SPLIT_KEY) || '');
        if (parsed && Number.isFinite(parsed.col) && Number.isFinite(parsed.row)) {
            return {
                col: clampSplit(parsed.col),
                row: clampSplit(parsed.row)
            };
        }
    } catch {
        // Ignore a bad stored size and use the default split.
    }
    return { col: 0.32, row: 0.5 };
}

function clampSplit(value) {
    return Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, value));
}

function PaneGrid({ children }) {
    const gridRef = useRef(null);
    const [split, setSplit] = useState(readSplit);

    useEffect(() => {
        sessionStorage.setItem(SPLIT_KEY, JSON.stringify(split));
    }, [split]);

    const dragSplit = (event, axis) => {
        event.preventDefault();
        const pointerId = event.pointerId;
        const handle = event.currentTarget;
        handle.setPointerCapture(pointerId);
        const move = (pointerEvent) => {
            const rect = gridRef.current?.getBoundingClientRect();
            if (!rect || rect.width <= 0 || rect.height <= 0) return;
            setSplit(current => ({
                col: axis === 'row' ? current.col : clampSplit((pointerEvent.clientX - rect.left) / rect.width),
                row: axis === 'col' ? current.row : clampSplit((pointerEvent.clientY - rect.top) / rect.height)
            }));
        };
        const stop = () => {
            if (handle.hasPointerCapture?.(pointerId)) {
                handle.releasePointerCapture(pointerId);
            }
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', stop);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
    };

    return (
        <div
            ref={gridRef}
            style={{
                display: 'grid',
                gridTemplateColumns: `${split.col}fr 10px ${1 - split.col}fr`,
                gridTemplateRows: `${split.row}fr 10px ${1 - split.row}fr`,
                minHeight: 0,
                flex: 1,
                overflow: 'hidden'
            }}
        >
            {children}
            <SplitHandle axis="col" onPointerDown={(event) => dragSplit(event, 'col')} style={{ gridColumn: 2, gridRow: '1 / -1' }} />
            <SplitHandle axis="row" onPointerDown={(event) => dragSplit(event, 'row')} style={{ gridColumn: '1 / -1', gridRow: 2, zIndex: 1 }} />
            <SplitHandle axis="both" onPointerDown={(event) => dragSplit(event, 'both')} style={{ gridColumn: 2, gridRow: 2, zIndex: 2 }} />
        </div>
    );
}

function SplitHandle({ axis, onPointerDown, style }) {
    const cursor = axis === 'col' ? 'col-resize' : axis === 'row' ? 'row-resize' : 'nwse-resize';
    const label = axis === 'both' ? 'Resize panes' : axis === 'col' ? 'Resize columns' : 'Resize rows';
    return (
        <div
            role="separator"
            aria-orientation={axis === 'row' ? 'horizontal' : 'vertical'}
            aria-label={label}
            title="Drag to resize"
            onPointerDown={onPointerDown}
            style={{
                ...style,
                cursor,
                touchAction: 'none',
                position: 'relative',
                zIndex: style.zIndex || 1
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    background: 'var(--border)',
                    borderRadius: '999px',
                    ...(axis === 'col'
                        ? { top: 8, bottom: 8, left: '50%', width: 2, transform: 'translateX(-50%)' }
                        : axis === 'row'
                            ? { left: 8, right: 8, top: '50%', height: 2, transform: 'translateY(-50%)' }
                            : { width: 8, height: 8, left: '50%', top: '50%', transform: 'translate(-50%, -50%)', background: 'var(--text-secondary)' })
                }}
            />
        </div>
    );
}

function Pane({ title, children, style }) {
    return (
        <section style={{ ...paneStyle, ...style }}>
            <div style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid var(--border)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                {title}
            </div>
            <div style={{ padding: '0.65rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', overflow: 'auto', minHeight: 0, flex: 1 }}>
                {children}
            </div>
        </section>
    );
}

function missingBadgeTitle(status, catalogFeature) {
    if (!status?.incomplete) return '';
    const featureLabelMissing = !String(catalogFeature?.label || '').trim();
    const valueLabelsMissing = (status.missingCount || 0) > 0;
    if (featureLabelMissing && valueLabelsMissing) {
        return 'This feature and one or more of its values have no display label.';
    }
    if (valueLabelsMissing) {
        return 'One or more values have no display label.';
    }
    return 'This feature has no display label.';
}

function ListButton({ active, label, badge, badgeTitle, badgeColor, onClick }) {
    const badgeClass = badgeColor === 'var(--error)' ? 'list-badge-error' : 'row-muted';
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rule-list-item-hover clickable-row${active ? ' is-selected' : ''}`}
            style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.45rem 0.5rem',
                border: 'none',
                fontFamily: 'inherit'
            }}
        >
            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{label}</span>
            {badge ? <span className={badgeClass} title={badgeTitle || undefined} style={{ fontSize: '0.65rem', flexShrink: 0 }}>{badge}</span> : null}
        </button>
    );
}

function EmptyNote({ children }) {
    return <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</p>;
}

function FieldLabel({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {label}
            {children}
        </label>
    );
}

function ValueEditor({ valueKey, entry, schema, onChange, onSetDefault }) {
    const propertyValues = entry.propertyValues || {};
    const setProperty = (key, next) => onChange({
        propertyValues: { ...propertyValues, [key]: next }
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h3 style={{ margin: 0, fontFamily: 'monospace' }}>{valueKey}</h3>
            <FieldLabel label="Label">
                <input className="input-field" value={entry.label || ''} onChange={event => onChange({ label: event.target.value })} style={{ margin: 0 }} />
            </FieldLabel>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={Boolean(entry.isDefault)} onChange={event => onSetDefault(event.target.checked)} />
                Default starting value
            </label>
            {schema.map(field => (
                <TemplateValueField
                    key={field.key}
                    field={field}
                    value={propertyValues[field.key]}
                    onChange={next => setProperty(field.key, next)}
                />
            ))}
            {schema.length === 0 && <EmptyNote>Add fields in the value template to collect more than the label.</EmptyNote>}
        </div>
    );
}

